import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { langDisplayName } from "@/lib/languages";

export const CEFR_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

// Shared CEFR level picker for one owned language profile (Settings + Dashboard).
export default function LevelDialog({ open, profile, onClose, onSaved }) {
  const { refresh } = useAuth();
  const { t, locale } = useI18n();
  const [level, setLevel] = useState(profile?.level);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setLevel(profile?.level); }, [open, profile]);
  if (!profile) return null;

  const save = async () => {
    if (level === profile.level) return onClose();
    setSaving(true);
    try {
      await api.patch(`/profiles/${profile.profile_id}`, { level });
      await refresh();
      toast.success(t("lvl.changed", { level }));
      onSaved?.(level);
      onClose();
    } catch { toast.error(t("lvl.failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto z-[70]" closeLabel={t("pm.consent.close")} data-testid="level-dialog">
        <DialogHeader>
          <DialogTitle className="font-cabinet text-xl text-start pe-6">{t("lvl.title", { lang: langDisplayName(profile.target_lang, locale) })}</DialogTitle>
          <DialogDescription className="text-start">{t("lvl.help")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2" role="radiogroup">
          {CEFR_LEVELS.map((l) => (
            <button key={l} type="button" role="radio" aria-checked={level === l} onClick={() => setLevel(l)}
              className={`w-full text-start flex items-start gap-3 border-2 border-ink rounded-xl px-3 py-2 transition-colors ${level === l ? "bg-mint" : "bg-white hover:bg-gray-50"}`}
              data-testid={`level-option-${l}`}>
              <span dir="ltr" className="font-cabinet font-extrabold w-8 shrink-0">{l}</span>
              <span className="min-w-0">
                <span className="font-bold text-sm flex items-center gap-2">{t(`lvl.levels.${l}.name`)}
                  {profile.level === l && <span className="nb-tag bg-white text-[10px] py-0">{t("lvl.current")}</span>}</span>
                <span className="block text-xs text-gray-600">{t(`lvl.levels.${l}.desc`)}</span>
              </span>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-600" data-testid="level-dialog-safe">{t("lvl.safe")}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="nb-btn-outline px-4 py-2 text-sm" data-testid="level-dialog-cancel">{t("lvl.cancel")}</button>
          <button onClick={save} disabled={saving} className="nb-btn-primary px-4 py-2 text-sm" data-testid="level-dialog-save">{saving ? t("lvl.saving") : t("lvl.save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Dashboard entry point: opens the same dialog for the ACTIVE profile.
export function ChangeLevelLink({ onSaved }) {
  const { t } = useI18n();
  const [profile, setProfile] = useState(null);
  const open = async () => {
    try {
      const r = await api.get("/profiles");
      setProfile(r.data.profiles.find((p) => p.profile_id === r.data.active_learning_profile_id) || r.data.profiles[0]);
    } catch { toast.error(t("lvl.failed")); }
  };
  return (
    <>
      <button onClick={open} className="text-sm font-bold underline underline-offset-2 hover:text-coral ms-2" data-testid="dash-change-level">{t("lvl.changeLevel")}</button>
      <LevelDialog open={!!profile} profile={profile} onClose={() => setProfile(null)} onSaved={onSaved} />
    </>
  );
}
